import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/store/shared/supabase";
import { dispatchDataError, describeSupabaseError } from "@/store/shared/dataErrors";
import { fetchScopeIds } from "@/store/shared/scopeIds";
import { useSession } from "@/session/SessionProvider";
import { createUuid } from "@/store/shared/id";
import type { Proprietaire, ProprietaireInput } from "./types";

interface ProprietairesContextValue {
  proprietaires: Proprietaire[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  addProprietaire: (input: ProprietaireInput) => Proprietaire;
  getProprietaire: (id: string) => Proprietaire | undefined;
  removeProprietaire: (id: string) => void;
  updateProprietaire: (id: string, input: ProprietaireInput) => void;
}

const ProprietairesContext = createContext<ProprietairesContextValue | null>(null);

type Row = Record<string, unknown>;
const str = (value: unknown): string => (typeof value === "string" ? value : "");
const nullable = (value: string): string | null => (value && value.trim() ? value : null);

/** Découpe un `nom_complet` en prénom (1er mot) + nom (le reste). */
function splitNomComplet(nomComplet: string): { prenom: string; nom: string } {
  const parts = nomComplet.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { prenom: "", nom: "" };
  if (parts.length === 1) return { prenom: parts[0], nom: "" };
  return { prenom: parts[0], nom: parts.slice(1).join(" ") };
}

function rowToProprietaire(row: Row): Proprietaire {
  const { prenom, nom } = splitNomComplet(str(row.nom_complet));
  return {
    id: str(row.id),
    prenom,
    nom,
    telephone: str(row.telephone),
    whatsapp: str(row.whatsapp),
    email: str(row.email),
    // Colonnes ajoutées au schéma `proprietaires` pour ne plus perdre ces informations.
    adresse: str(row.adresse),
    notes: str(row.notes),
    statut: (str(row.statut) || "actif") as Proprietaire["statut"],
    createdAt: str(row.created_at),
  };
}

/**
 * Propriétaires gérés par l'agence — branché sur Supabase.
 *
 * La table `proprietaires` permet `compte_id = NULL` : une agence peut créer une
 * simple fiche propriétaire SANS compte Auth. L'`agence_id` est imposé côté
 * serveur (`current_agence_id()`), jamais choisi par le navigateur.
 */
export function ProprietairesProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [proprietaires, setProprietaires] = useState<Proprietaire[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from("proprietaires")
        .select("*")
        .order("created_at", { ascending: false });
      if (fetchError) throw fetchError;
      setProprietaires(((data as Row[] | null) ?? []).map(rowToProprietaire));
    } catch (caught) {
      setError(describeSupabaseError(caught));
      console.error("[Propriétaires] chargement Supabase échoué", caught);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!session || (session.role !== "dg_agence" && session.role !== "employe_agence")) {
      setProprietaires([]);
      return;
    }
    void load();
  }, [session, load]);

  const addProprietaire = useCallback(
    (input: ProprietaireInput) => {
      const optimistic: Proprietaire = { ...input, id: createUuid(), createdAt: new Date().toISOString() };
      setProprietaires((prev) => [optimistic, ...prev]);

      void (async () => {
        try {
          const scope = await fetchScopeIds();
          const payload = {
            nom_complet: `${input.prenom} ${input.nom}`.trim(),
            telephone: nullable(input.telephone),
            whatsapp: nullable(input.whatsapp),
            email: nullable(input.email),
            adresse: nullable(input.adresse),
            notes: nullable(input.notes),
            statut: input.statut,
            agence_id: scope.agenceId,
            compte_id: null,
          };
          const { data, error: insertError } = await supabase
            .from("proprietaires")
            .insert(payload)
            .select("id")
            .maybeSingle();
          if (insertError) throw insertError;
          const realId = (data as Row | null)?.id;
          if (realId) {
            setProprietaires((prev) =>
              prev.map((item) => (item.id === optimistic.id ? { ...item, id: String(realId) } : item))
            );
          }
          void load();
        } catch (caught) {
          setProprietaires((prev) => prev.filter((item) => item.id !== optimistic.id));
          dispatchDataError({
            scope: "Propriétaires",
            message: "Impossible de créer la fiche propriétaire.",
            technical: describeSupabaseError(caught),
          });
        }
      })();

      return optimistic;
    },
    [load]
  );

  const updateProprietaire = useCallback(
    (id: string, input: ProprietaireInput) => {
      const previous = proprietaires.find((item) => item.id === id);
      setProprietaires((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));
      void (async () => {
        const payload = {
          nom_complet: `${input.prenom} ${input.nom}`.trim(),
          telephone: nullable(input.telephone),
          whatsapp: nullable(input.whatsapp),
          email: nullable(input.email),
          adresse: nullable(input.adresse),
          notes: nullable(input.notes),
          statut: input.statut,
        };
        const { error: updateError } = await supabase.from("proprietaires").update(payload).eq("id", id);
        if (updateError) {
          if (previous) setProprietaires((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Propriétaires",
            message: "Impossible de modifier la fiche propriétaire.",
            technical: describeSupabaseError(updateError),
          });
          return;
        }
        void load();
      })();
    },
    [proprietaires, load]
  );

  const removeProprietaire = useCallback(
    (id: string) => {
      const previous = proprietaires.find((item) => item.id === id);
      setProprietaires((prev) => prev.filter((item) => item.id !== id));
      void (async () => {
        const { error: deleteError } = await supabase.from("proprietaires").delete().eq("id", id);
        if (deleteError) {
          if (previous) setProprietaires((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Propriétaires",
            message: "Impossible de supprimer la fiche (des immeubles y sont peut-être rattachés).",
            technical: describeSupabaseError(deleteError),
          });
          return;
        }
        void load();
      })();
    },
    [proprietaires, load]
  );

  const getProprietaire = useCallback(
    (id: string) => proprietaires.find((item) => item.id === id),
    [proprietaires]
  );

  const value = useMemo(
    () => ({
      proprietaires,
      loading,
      error,
      reload: load,
      addProprietaire,
      getProprietaire,
      removeProprietaire,
      updateProprietaire,
    }),
    [proprietaires, loading, error, load, addProprietaire, getProprietaire, removeProprietaire, updateProprietaire]
  );

  return <ProprietairesContext.Provider value={value}>{children}</ProprietairesContext.Provider>;
}

export function useProprietaires(): ProprietairesContextValue {
  const context = useContext(ProprietairesContext);
  if (!context) {
    throw new Error("useProprietaires doit être utilisé à l'intérieur de ProprietairesProvider");
  }
  return context;
}